import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'
import { Profile } from '../../types/profile'
import { useContextHook } from '../../store/context'
import { MachineSlot } from '../../types/machineslot'
import { Checkbox, IconButton, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import { toReadableTimestamp } from '../../utils/dateUtils'
import ProfileTypeChip from './profileTypeChip'
import CloseIcon from '@mui/icons-material/Close'
import CircleIcon from '@mui/icons-material/Circle'

interface AssignDialogProps {
  onClose: () => void
  assignBBP: (b: Profile) => void
  selectedProfiles: Profile[]
  machineSlots: MachineSlot[]
  handleAssign: (profiles: Profile[]) => void
  updateUnassignedProfilesAlert: (profiles: Profile[]) => void
}

const AssignDialog = (props: AssignDialogProps) => {
  const { t } = useTranslation()
  const { state } = useContextHook()

  const [bbps, setBbps] = React.useState<Profile[]>([])
  const [profiles, setProfiles] = React.useState<Profile[]>([])
  const [seletedBbp, setSelectedBbp] = React.useState<Profile>()
  const [unselectedProfiles, setUnselectedProfiles] = React.useState<Profile[]>([])
  const [renderTooManyProfiles, setRenderTooManyProfiles] = React.useState<boolean>(false)
  const [renderSelectSlots, setRenderSelectSlots] = React.useState<boolean>(false)
  const [renderSelectBbp, setRenderSelectBbp] = React.useState<boolean>(false)
  const [selectedSlot, setSelectedSlot] = React.useState<MachineSlot[]>([])

  const seperateProfilesAndBbp = useCallback(() => {
    const b = props.selectedProfiles.filter((x) => x.is_bbp_profile === true)
    const p = props.selectedProfiles.filter((x) => x.is_bbp_profile === false)
    return { p, b }
  }, [props.selectedProfiles])

  const getAssignedSlots = useCallback((): Array<MachineSlot> => {
    return props.machineSlots.filter((x) => !!x.requested_profile_data)
  }, [props.machineSlots])

  const machineSlotMaxCount = state.currentMachine?.profile_slot_count || 0

  React.useEffect(() => {
    const { p, b } = seperateProfilesAndBbp()
    setBbps(b)
    if (b.length > 1) {
      setRenderSelectBbp(true)
    }
    if (p.length > machineSlotMaxCount) {
      setRenderTooManyProfiles(true)
    }
    if (p.some((p) => p.machine_slot_index === undefined)) {
      setRenderSelectSlots(true)
    }
    setProfiles(p)
  }, [props.selectedProfiles, seperateProfilesAndBbp, seletedBbp, getAssignedSlots, machineSlotMaxCount])

  React.useEffect(() => {
    if (profiles.length > machineSlotMaxCount) {
      setRenderTooManyProfiles(true)
      return
    }
    if (profiles.some((p) => p.machine_slot_index === undefined)) {
      setRenderSelectSlots(true)
      return
    }
    if (
      profiles.length > 0 &&
      profiles.length <= machineSlotMaxCount &&
      !profiles.some((p) => p.machine_slot_index === undefined)
    ) {
      props.handleAssign(profiles)
    }
  }, [bbps.length, machineSlotMaxCount, profiles, props, renderSelectSlots, renderTooManyProfiles])

  const handleAssign = () => {
    const unassignedProfilesAlert: Profile[] = selectedSlot
      .filter((s) => s.requested_profile_data !== null && s.requested_profile_data !== undefined)
      .map((s) => s.requested_profile_data!)
    props.updateUnassignedProfilesAlert(unassignedProfilesAlert)
    let i = 0
    const newList = profiles
    for (i; i < selectedSlot.length; i++) {
      newList[newList.findIndex((p) => p.machine_slot_index === undefined)].machine_slot_index =
        selectedSlot[i].slot_index
    }
    props.handleAssign(newList)
  }

  const findAndFIllFreeSlots = (profiles: Profile[]): Profile[] => {
    let i = 0
    const newList = profiles
    const free_slots = unselectedProfiles.filter((p) => p.machine_slot_index !== undefined)
    for (i; i < free_slots.length; i++) {
      const index = newList.findIndex((p) => p.machine_slot_index === undefined)
      if (index >= 0) {
        newList[index].machine_slot_index = free_slots[i].machine_slot_index
      }
    }
    return newList
  }

  return (
    <Dialog open={true} maxWidth={'md'} fullWidth={true}>
      {renderSelectBbp && (
        <>
          <DialogTitle sx={{ minWidth: '100%' }}>
            <Typography variant="h5" sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {t('profilesview.replacebbpprofile').replace(
                '{0}',
                state.currentMachine?.bbp_profile ? 'Replace' : 'Assign'
              )}
              <IconButton onClick={props.onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              {t('profilesview.assignorreplacebbp')
                .replace('{0}', bbps.length.toString())
                .replace('{1}', state.currentMachine?.bbp_profile ? 'replace' : 'assign')}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Table>
              <TableBody>
                {bbps.map((b) => {
                  return (
                    <TableRow key={b.id} selected={seletedBbp === b ? true : false} hover={true} sx={{ boxShadow: 1 }}>
                      <TableCell>
                        <Typography sx={{ textTransform: 'uppercase', color: 'secondary.contrastText' }}>
                          {b.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ProfileTypeChip profile={b} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{toReadableTimestamp(b.modified)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={b.url === seletedBbp?.url}
                          onClick={() => {
                            setSelectedBbp(b)
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions sx={{ px: 5, pb: 5 }}>
            <Button
              variant="contained"
              disabled={!seletedBbp}
              onClick={() => {
                if (seletedBbp) {
                  props.assignBBP(seletedBbp)
                  setRenderSelectBbp(false)
                  if (profiles.length < 1) {
                    props.onClose()
                  }
                }
              }}
            >
              {t('profilesview.assigntomachine')}
            </Button>
          </DialogActions>
        </>
      )}
      {renderTooManyProfiles && bbps.length === 0 && (
        <>
          <DialogTitle>
            <Typography variant="h4" sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {t('profilesview.unselectprofiles')}
              <IconButton onClick={props.onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Typography>
            <Typography variant="subtitle2">
              {t('profilesview.unselectsubtitle')
                .replace('{1}', machineSlotMaxCount.toString())
                .replace('{2}', (profiles.length - machineSlotMaxCount).toString())}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Table>
              <TableBody>
                {profiles.map((profile) => {
                  return (
                    <TableRow
                      key={profile.id}
                      selected={unselectedProfiles.includes(profile) ? false : true}
                      hover={true}
                      sx={{ boxShadow: 1 }}
                    >
                      <TableCell>
                        <Typography sx={{ textTransform: 'uppercase', color: 'secondary.contrastText', left: 0 }}>
                          {profile.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ left: 2 }}>
                          <ProfileTypeChip profile={profile} />
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{toReadableTimestamp(profile.modified)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={!unselectedProfiles.includes(profile)}
                          onClick={() => {
                            if (unselectedProfiles.includes(profile)) {
                              const newList = unselectedProfiles.filter((p) => p !== profile)
                              setUnselectedProfiles(newList)
                            } else {
                              setUnselectedProfiles([...unselectedProfiles, profile])
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions sx={{ px: 5, pb: 5 }}>
            <Button
              disabled={profiles.length - unselectedProfiles.length > machineSlotMaxCount}
              variant="contained"
              onClick={() => {
                let newList = profiles.filter((p) => !unselectedProfiles.includes(p))
                newList = findAndFIllFreeSlots(newList)
                setProfiles(newList)
                setRenderTooManyProfiles(false)
              }}
            >
              {t('profilesview.assigntomachine')}
            </Button>
          </DialogActions>
        </>
      )}
      {renderSelectSlots && !renderTooManyProfiles && bbps.length === 0 && (
        <>
          <DialogTitle>
            <Typography variant="h4" sx={{ display: 'flex', justifyContent: 'space-between' }}>
              {t('profilesview.makespace')}
              <IconButton onClick={props.onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Typography>
            <Typography variant="body2">
              {t('profilesview.notenoughfreeslots')
                .replace('{0}', (machineSlotMaxCount - getAssignedSlots().length).toString())
                .replace('{1}', profiles.length.toString())
                .replace('{2}', (profiles.length - (machineSlotMaxCount - getAssignedSlots().length)).toString())}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Table>
              <TableBody>
                {props.machineSlots
                  .filter((m) => m.requested_profile_data)
                  .map((machineslot) => {
                    return (
                      <TableRow
                        key={machineslot.slot_index}
                        selected={selectedSlot.includes(machineslot) ? true : false}
                        hover={true}
                        sx={{ boxShadow: 1 }}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center">
                            <Typography>{machineslot.slot_index + 1}</Typography>
                            <CircleIcon fontSize="small" color={machineslot.loaded_timestamp ? 'success' : 'error'} />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ textTransform: 'uppercase', color: 'secondary.contrastText' }}>
                            {machineslot.requested_profile_data?.name}
                          </Typography>
                        </TableCell>
                        {machineslot.requested_profile_data && (
                          <>
                            <TableCell>
                              <ProfileTypeChip profile={machineslot.requested_profile_data} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {toReadableTimestamp(machineslot.requested_profile_data.modified)}
                              </Typography>
                            </TableCell>
                          </>
                        )}
                        <TableCell>
                          <Checkbox
                            sx={{ '&.Mui-checked': 'red' }}
                            checked={selectedSlot.includes(machineslot)}
                            onClick={() => {
                              if (selectedSlot.includes(machineslot)) {
                                const newList = selectedSlot.filter((m) => m !== machineslot)
                                setSelectedSlot(newList)
                              } else {
                                setSelectedSlot([...selectedSlot, machineslot])
                              }
                            }}
                            disabled={
                              profiles.length - (props.machineSlots.length - getAssignedSlots().length) ===
                                selectedSlot.length && !selectedSlot.includes(machineslot)
                            }
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions sx={{ px: 5, pb: 5 }}>
            <Button
              variant="contained"
              disabled={profiles.length > props.machineSlots.length - getAssignedSlots().length + selectedSlot.length}
              onClick={handleAssign}
            >
              {t('profilesview.assigntomachine')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
export default AssignDialog
